"use client";

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Play, X } from "lucide-react";
import { Button } from "@/components/ui/button";

function VideoModal({
  videoId,
  onClose,
}: {
  videoId: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[color-mix(in_srgb,var(--foreground)_55%,transparent)] p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Greip explainer video"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon-lg"
        aria-label="Close video"
        className="absolute top-4 right-4 text-white hover:bg-white/15 hover:text-white"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
      >
        <X className="size-5" aria-hidden />
      </Button>
      <div
        className="relative aspect-video w-full max-w-4xl overflow-hidden rounded-xl border border-line bg-black"
        onClick={(event) => event.stopPropagation()}
      >
        <iframe
          src={`https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0`}
          title="Greip explainer video"
          className="absolute inset-0 h-full w-full"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
}

export default function HeroVideoButton({ videoId }: { videoId: string }) {
  const [videoOpen, setVideoOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const closeVideo = useCallback(() => setVideoOpen(false), []);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon-lg"
        aria-label="Play Greip explainer video"
        className="hero-play-btn rounded-full bg-[rgb(255_255_255/0.82)] hover:bg-[rgb(255_255_255/0.96)] hover:text-foreground"
        onClick={() => setVideoOpen(true)}
      >
        <Play className="size-3.5 fill-current" />
      </Button>

      {videoOpen && mounted
        ? createPortal(
            <VideoModal videoId={videoId} onClose={closeVideo} />,
            document.body,
          )
        : null}
    </>
  );
}
